import EditIcon from '@mui/icons-material/Edit'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  useGetAllInventoryPartsQuery,
  useGetVendorEntriesByIdQuery,
  useUpdateInventoryInstanceMutation
} from '../../../redux/api'
import { InventoryPartInstanceEntry } from '../../../sdk'
import { formatCurrency } from '../../../utils'
import TextFieldNoArrowNoScroll from '../../FormField/TextFieldNoArrowNoScroll'

interface Props {
  instance: InventoryPartInstanceEntry
}

const CounterSaleEditInventoryInCart = ({ instance }: Props) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { data: allShopInventory } = useGetAllInventoryPartsQuery(
    {
      authorization: accessToken,
      shop: shopId,
      stockedOnly: false,
      isFluidsOnly: false,
      partId: [instance.info!.inventoryPartId!]
    },
    { skip: !instance.info?.inventoryPartId }
  )
  const inventoryPart =
    allShopInventory?.allParts![instance.info?.inventoryPartId!]
  const [price, setPrice] = useState<number | ''>('')
  useEffect(
    () => setPrice(instance.info?.counterSalePricePerUnit || 0),
    [instance.info?.counterSalePricePerUnit]
  )
  const [open, setOpen] = useState(false)

  const [updateInventoryInstance, { isLoading }] =
    useUpdateInventoryInstanceMutation()

  const updateInstance = useCallback(async () => {
    await updateInventoryInstance({
      authorization: accessToken,
      inventoryPartInstanceEntry: {
        id: instance.id,
        info: {
          ...instance.info,
          counterSalePricePerUnit: price || 0
        }
      }
    })
    setOpen(false)
  }, [accessToken, instance.id, instance.info, price, updateInventoryInstance])

  const { data: vendors } = useGetVendorEntriesByIdQuery(
    {
      authorization: accessToken,
      shop: shopId,
      vendorIds: instance.info?.vendorId ? [instance.info?.vendorId] : []
    },
    {
      skip: !instance.info?.vendorId
    }
  )
  const vendorName = vendors && vendors[0].info?.name

  return (
    <>
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle>{inventoryPart?.info?.name}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Part number: ${inventoryPart?.info?.partNumber}`}
          </DialogContentText>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice Number</TableCell>
                  <TableCell>Unit Cost</TableCell>
                  {inventoryPart?.info?.isFluids && (
                    <>
                      <TableCell>Volume</TableCell>
                      <TableCell>Unit of Measure</TableCell>
                    </>
                  )}
                  <TableCell>Retail Price</TableCell>
                  <TableCell>Vendor Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell>{instance.info?.invoiceNumber}</TableCell>
                  <TableCell>
                    {formatCurrency(instance.info?.cost || 0, true)}
                  </TableCell>
                  {inventoryPart?.info?.isFluids && (
                    <>
                      <TableCell>{instance.info?.quantity}</TableCell>
                      <TableCell>{instance.info?.unitOfMeasure}</TableCell>
                    </>
                  )}
                  <TableCell>
                    <TextFieldNoArrowNoScroll
                      label="Price"
                      value={price}
                      error={price !== '' && price < 0}
                      helperText={
                        price !== '' && price < 0
                          ? 'Price cannot be negative'
                          : ''
                      }
                      type="number"
                      onChange={(event) => {
                        const newValue = parseFloat(event.target.value)
                        if (isNaN(newValue)) {
                          setPrice('')
                        } else {
                          setPrice(newValue)
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        )
                      }}
                    />
                  </TableCell>
                  <TableCell>{vendorName}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={isLoading} onClick={updateInstance}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <IconButton onClick={() => setOpen(true)}>
        <EditIcon />
      </IconButton>
    </>
  )
}

export default CounterSaleEditInventoryInCart
